import React, { useEffect, useMemo, useState } from "react";
import "./../../../../assets/css/purchase/form.css";

// Components
import Breadcrumbs from "../../../components/Breadcrumbs";
import FormAction from "../../../components/Actions/FormAction";

// Utils
import {
  deleteData,
  getAlert,
  insertData,
  updateData,
} from "../../../../utils/SamuiUtils";

const WHForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
  masterList = [],
  setMode,
}) => {
  const [initialFormData, setInitialFormData] = useState({});

  const companyId = useMemo(
    () => window.localStorage.getItem("company") || "",
    []
  );

  const hasCompIdField = useMemo(() => {
    if (
      selectedData &&
      Object.prototype.hasOwnProperty.call(selectedData, "Comp_Id")
    ) {
      return true;
    }
    if (Array.isArray(masterList) && masterList.length > 0) {
      return masterList.some((item) =>
        Object.prototype.hasOwnProperty.call(item || {}, "Comp_Id")
      );
    }
    return false;
  }, [masterList, selectedData]);

  const goBackToList = () => {
    if (typeof setMode === "function") {
      setMode("S");
    } else {
      window.location.href = "/tbsetwh";
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target?.tagName === "INPUT" ||
        event.target?.tagName === "TEXTAREA" ||
        event.target?.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            goBackToList();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
          if (mode === "I") return;
          if (mode === "U") {
            confirmAndUpdate();
          }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, formData]);

  useEffect(() => {
    if (mode === "I") {
      const defaultData = {
        WH_Code: formData?.WH_Code || "",
        WH_Name: "",
        WH_Status: "Y",
        ...(hasCompIdField ? { Comp_Id: companyId || undefined } : {}),
      };
      setFormData(defaultData);
      setInitialFormData(defaultData);
    } else if (mode === "U" && selectedData) {
      const existingData = {
        WH_Id: selectedData.WH_Id,
        WH_Code: selectedData.WH_Code || "",
        WH_Name: selectedData.WH_Name || "",
        WH_Status: selectedData.WH_Status || "Y",
        ...(hasCompIdField
          ? { Comp_Id: selectedData.Comp_Id || companyId || undefined }
          : {}),
      };
      setFormData(existingData);
      setInitialFormData(existingData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedData, hasCompIdField]);

  const handleChange = (event) => {
    const { name: fieldName, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const sanitizePayload = (data) => {
    const payload = { ...data };

    if (payload.WH_Code) {
      payload.WH_Code = payload.WH_Code.trim();
    }

    payload.WH_Name = (payload.WH_Name || "").trim();
    payload.WH_Status = payload.WH_Status === "N" ? "N" : "Y";

    if (!hasCompIdField || !payload.Comp_Id) {
      delete payload.Comp_Id;
    }

    return payload;
  };

  const buildInsertArguments = (data) => {
    const prepared = { ...data };
    delete prepared.WH_Id;
    const entries = Object.entries(prepared).filter(([, v]) => v !== undefined);
    const fields = entries.map(([key]) => key).join(", ");
    const values = entries.map(([, value]) => value);
    return { fields, values };
  };

  const buildUpdateValues = (data) => {
    const prepared = { ...data };
    delete prepared.WH_Id;
    Object.keys(prepared).forEach((key) => {
      if (prepared[key] === undefined) {
        delete prepared[key];
      }
    });
    return prepared;
  };

  const handleInsertWH = async (payload) => {
    const { fields, values } = buildInsertArguments(payload);
    const response = await insertData("Tb_Set_WH", fields, values);
    if (response?.status === "OK") {
      getAlert("OK", "บันทึกข้อมูลสำเร็จ");
      await callInitialize();
    } else {
      getAlert("FAILED", response?.message || "ไม่สามารถบันทึกข้อมูลได้");
    }
  };

  const handleUpdateWH = async (payload) => {
    const where = { WH_Id: payload.WH_Id };
    const values = buildUpdateValues(payload);
    await updateData("Tb_Set_WH", values, where);
    getAlert("OK", "อัปเดตข้อมูลสำเร็จ");
    await callInitialize(payload.WH_Id);
  };

  const handleDelete = async () => {
    if (!selectedData?.WH_Id) {
      getAlert("WARNING", "ไม่พบข้อมูลที่ต้องการลบ");
      return;
    }

    getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
      if (!isConfirm) return;
      try {
        const targetCompId =
          selectedData?.Comp_Id || companyId || localStorage.getItem("company");
        const whereConditions = [`WH_Id = ${selectedData.WH_Id}`];

        if (targetCompId) {
          whereConditions.push(`Comp_Id = '${targetCompId}'`);
        }

        const res = await deleteData(
          "Tb_Set_WH",
          `WHERE ${whereConditions.join(" AND ")}`
        );

        getAlert("OK", "ลบข้อมูลสำเร็จ");
        await callInitialize();

      } catch (error) {
        getAlert(
          "FAILED",
          error?.message || "ไม่สามารถลบข้อมูลได้ กรุณาลองใหม่อีกครั้ง"
        );
      }
    });
  };

  const confirmAndSubmit = async () => {
    try {
      getAlert("C_DIALOG", "ยืนยันการบันทึกข้อมูล", async (isConfirm) => {
        if (isConfirm) {
          await handleSubmit();
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const confirmAndUpdate = async () => {
    try {
      getAlert("C_DIALOG", "ยืนยันการแก้ไขข้อมูล", async (isConfirm) => {
        if (isConfirm) {
          await handleSubmit();
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleReset = () => {
    if (Object.keys(initialFormData).length === 0) return;
    setFormData(initialFormData);
  };

  const handleSubmit = async () => {
    const payload = sanitizePayload({
      ...formData,
      Comp_Id: formData.Comp_Id || companyId || undefined,
    });

    if (!payload.WH_Name) {
      getAlert("WARNING", "กรุณากรอกชื่อคลัง");
      return;
    }

    if (payload.WH_Code && payload.WH_Code.length > 50) {
      getAlert("WARNING", "รหัสคลังต้องไม่เกิน 50 ตัวอักษร");
      return;
    }

    const duplicateByCode = masterList.some((item) => {
      if (!payload.WH_Code) return false;
      if (mode === "U" && item.WH_Id === payload.WH_Id) return false;
      return (
        (item.WH_Code || "").trim().toLowerCase() ===
        payload.WH_Code.trim().toLowerCase()
      );
    });

    if (duplicateByCode) {
      getAlert("WARNING", "รหัสคลังนี้มีอยู่ในระบบแล้ว");
      return;
    }

    const duplicateByName = masterList.some((item) => {
      if (mode === "U" && item.WH_Id === payload.WH_Id) return false;
      return (
        (item.WH_Name || "").trim().toLowerCase() ===
        payload.WH_Name.trim().toLowerCase()
      );
    });

    if (duplicateByName) {
      getAlert("WARNING", "ชื่อคลังนี้มีอยู่ในระบบแล้ว");
      return;
    }

    try {
      if (mode === "I") {
        await handleInsertWH(payload);
      } else if (mode === "U") {
        await handleUpdateWH(payload);
      }
    } catch (error) {
      getAlert("FAILED", error.message || "ไม่สามารถบันทึกข้อมูลได้");
    }
  };

  return (
    <>
      <Breadcrumbs
        isShowStatus={mode === "U"}
        items={[
          { name: "ข้อมูลตั้งต้น", url: "/initialdata" },
          { name: name, url: "/tbsetwh" },
          {
            name: mode === "U" ? `แก้ไข${name}` : `สร้าง${name}`,
            url: "#",
          },
        ]}
      />

      <div className="container py-4">
        <div className="card shadow-sm">
          <div className="card-body">
            <h5 className="mb-3 border-bottom pb-2">ข้อมูลคลังสินค้า</h5>
            <div className="row g-3">
              <div className="col-4">
                <label>รหัสคลัง</label>
                <input
                  type="text"
                  className="form-control"
                  name="WH_Code"
                  value={formData?.WH_Code || ""}
                  onChange={handleChange}
                  maxLength={50}
                  disabled
                />
              </div>

              <div className="col-8">
                <label>
                  ชื่อคลัง <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="WH_Name"
                  value={formData?.WH_Name || ""}
                  onChange={handleChange}
                />
              </div>

              <div className="col-4">
                <label>สถานะ</label>
                <select
                  className="form-select"
                  name="WH_Status"
                  value={formData?.WH_Status || "Y"}
                  onChange={handleChange}
                >
                  <option value="Y">ใช้งาน</option>
                  <option value="N">ไม่ใช้งาน</option>
                </select>
              </div>
            </div>

            <div className="row mt-4">
              {mode === "U" ? (
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      onClick={goBackToList}
                      type="button"
                      className="btn btn-dark btn-lg"
                    >
                      กลับค้นหา
                    </button>
                    <button
                      onClick={handleReset}
                      type="button"
                      className="btn btn-info btn-lg text-white"
                    >
                      เริ่มใหม่
                    </button>
                    <button
                      onClick={handleDelete}
                      type="button"
                      className="btn btn-danger btn-lg"
                    >
                      ลบ
                    </button>
                  </div>
                  <div>
                    <button
                      onClick={confirmAndUpdate}
                      type="button"
                      className="btn btn-warning text-white btn-lg"
                    >
                      บันทึก
                    </button>
                  </div>
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-2">
                  <button
                    onClick={goBackToList}
                    type="button"
                    className="btn btn-dark btn-lg"
                  >
                    กลับค้นหา
                  </button>
                  <button
                    onClick={handleReset}
                    type="button"
                    className="btn btn-info btn-lg text-white"
                  >
                    เริ่มใหม่
                  </button>
                  <FormAction
                    onSubmit={handleSubmit}
                    onUpdate={handleSubmit}
                    onCancel={goBackToList}
                    mode={mode}
                    setMode={setMode}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WHForm;
